import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { IndianRupee, Plus, Repeat, Calendar as CalendarIcon, Ban, Trash2, CheckCircle2 } from 'lucide-react';

const inr = (n) => n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const KIND_OPTIONS = [
  { v: 'loan', label: 'Loan / EMI' },
  { v: 'advance', label: 'Salary Advance' },
  { v: 'damage', label: 'Damage / Penalty' },
  { v: 'pf', label: 'Provident Fund' },
  { v: 'tax', label: 'Tax (manual)' },
  { v: 'other', label: 'Other' },
];

const blank = { kind: 'loan', label: '', amount: '', recurring: false, effective_from: new Date().toISOString().slice(0, 10), notes: '' };

export default function ManualDeductionsSection({ employeeId, canEdit }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/employees/${employeeId}/deductions`); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { if (employeeId) load(); }, [employeeId]); // eslint-disable-line

  const add = async () => {
    const amt = +form.amount;
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    if (!form.label.trim()) return toast.error('Add a short label (e.g. "Office laptop EMI")');
    setBusy(true);
    try {
      await api.post(`/employees/${employeeId}/deductions`, { ...form, amount: amt });
      toast.success(form.recurring ? 'Recurring deduction added' : 'One-time deduction scheduled');
      setOpen(false); setForm(blank); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancel = async (d) => {
    if (!window.confirm(`Cancel deduction "${d.label}"? It will no longer apply.`)) return;
    try { await api.patch(`/employees/${employeeId}/deductions/${d.id}/cancel`); toast.success('Deduction cancelled'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete deduction "${d.label}"?`)) return;
    try { await api.delete(`/employees/${employeeId}/deductions/${d.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const active = items.filter(i => i.active);
  const inactive = items.filter(i => !i.active);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="manual-deductions-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Payroll</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">Manual deductions</h2>
          <p className="text-xs text-slate-500 mt-0.5">Recurring or one-time amounts that will be subtracted from this employee's payslip.</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(blank); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-deduction-button" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                <Plus className="w-4 h-4 mr-2" /> Add deduction
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="deduction-dialog">
              <DialogHeader><DialogTitle className="font-heading text-2xl">New deduction</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Type *</Label>
                    <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                      <SelectTrigger data-testid="ded-kind" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {KIND_OPTIONS.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Amount (₹) *</Label>
                    <Input data-testid="ded-amount" type="number" min="1" value={form.amount}
                           onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Label *</Label>
                  <Input data-testid="ded-label" value={form.label}
                         onChange={(e) => setForm({ ...form, label: e.target.value })}
                         placeholder="e.g. Office laptop EMI" className="rounded-xl mt-1" />
                </div>
                <div className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl p-3">
                  <input id="ded-recurring" type="checkbox" data-testid="ded-recurring" checked={form.recurring}
                         onChange={(e) => setForm({ ...form, recurring: e.target.checked })} className="w-4 h-4" />
                  <label htmlFor="ded-recurring" className="text-sm cursor-pointer">
                    <b>Recurring</b> — apply every month until cancelled.
                    <span className="block text-xs text-slate-500">Leave unchecked for a one-time deduction.</span>
                  </label>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Effective from</Label>
                  <Input data-testid="ded-effective-from" type="date" value={form.effective_from}
                         onChange={(e) => setForm({ ...form, effective_from: e.target.value })} className="rounded-xl mt-1" />
                  <p className="text-[11px] text-slate-500 mt-1">
                    {form.recurring ? 'Applies to every monthly payslip starting from this date.' : 'Applies to the payslip of the month containing this date — then auto-archives.'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Notes</Label>
                  <Textarea data-testid="ded-notes" value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-xl mt-1" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                <Button onClick={add} disabled={busy} data-testid="submit-deduction" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                  {busy ? 'Saving…' : 'Add deduction'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm" data-testid="no-deductions">
          <IndianRupee className="w-8 h-8 mx-auto opacity-30 mb-2" />
          No manual deductions on file.
        </div>
      ) : (
        <div className="space-y-2">
          {active.map(d => <DedRow key={d.id} d={d} canEdit={canEdit} onCancel={cancel} onRemove={remove} />)}
          {inactive.length > 0 && (
            <div className="pt-3 mt-3 border-t border-slate-100">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-2">Past / cancelled</div>
              <div className="space-y-2">
                {inactive.map(d => <DedRow key={d.id} d={d} canEdit={canEdit} inactive onRemove={remove} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const DedRow = ({ d, canEdit, inactive, onCancel, onRemove }) => (
  <div data-testid={`deduction-${d.id}`} className={`flex items-start justify-between gap-3 rounded-xl border p-3 ${inactive ? 'bg-slate-50/60 border-slate-100 opacity-70' : 'bg-white border-slate-200'}`}>
    <div className="flex items-start gap-3 min-w-0 flex-1">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${d.recurring ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
        {d.recurring ? <Repeat className="w-4 h-4" /> : <CalendarIcon className="w-4 h-4" />}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900">{d.label}</span>
          <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{d.kind}</span>
          <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${d.recurring ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
            {d.recurring ? 'Recurring' : 'One-time'}
          </span>
          {!d.active && d.consumed_in_month && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Applied in {d.consumed_in_month}
            </span>
          )}
          {!d.active && !d.consumed_in_month && (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-700">Cancelled</span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">From {d.effective_from || '—'} · {d.created_by_name && `by ${d.created_by_name}`}</div>
        {d.notes && <div className="text-xs text-slate-600 mt-1 italic">{d.notes}</div>}
      </div>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <div className="font-heading font-bold text-lg text-slate-900 tabular-nums">{inr(d.amount)}</div>
      {canEdit && d.active && (
        <button data-testid={`cancel-deduction-${d.id}`} onClick={() => onCancel(d)} title="Cancel" className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-slate-50"><Ban className="w-4 h-4" /></button>
      )}
      {canEdit && !d.active && (
        <button data-testid={`delete-deduction-${d.id}`} onClick={() => onRemove(d)} title="Delete" className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-slate-50"><Trash2 className="w-4 h-4" /></button>
      )}
    </div>
  </div>
);
